import * as React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography, makeStyles, Button, Collapse, Grid } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { DateTime } from 'luxon'
import { classNames } from '../shared';
import AudCurrencyFormatter from '../utilities/currencyFormatter';
import { TaxMode, ApplicationSettingsSelectors, useApplicationSettingsSelector } from '../store/modules/applicationSettings';
import { red } from '@material-ui/core/colors';
import { PostingTypeEnum } from 'src/api/dtos';

interface IJournalEntryLineViewProps {
    journalEntryLineIndex: number;
    journalEntryLinePosition: number;
}

const useStyles = makeStyles(theme => ({
    error: {
        background: `${red[100]} !important`
    },
    button: {
        textAlign: 'center',
        minWidth: 'auto',
        padding: theme.spacing(1),

        '& svg': {
            transition: 'all 0.25s linear'
        }
    },
    expanded: {
        '& svg': {
            transform: 'rotate(180deg)'
        }
    },
    noPadding: {
        padding: theme.spacing(0)
    },
    padding: {
        padding: theme.spacing(2)
    },
    pre: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        lineHeight: 1.43,
        fontSize: '0.875rem'
    }
}))

const JournalEntryLineView: React.FunctionComponent<IJournalEntryLineViewProps> = ({
    journalEntryLineIndex,
    journalEntryLinePosition
}) => {
    const journalEntryLine = useIifImportSelector(state => IifImportSelectors.selectJournalEntryLine(state, journalEntryLineIndex));

    const taxMode = useApplicationSettingsSelector(ApplicationSettingsSelectors.selectTaxMode);

    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const onExpandClick = React.useCallback(() => {
        if (expanded) {
            setShow(!show);
        }
        else {
            setExpanded(true);
            setShow(true);
        }
    }, [setShow, show, expanded, setExpanded]);

    const onExited = React.useCallback(() => {
        setExpanded(false);
    }, [setExpanded])

    if (!journalEntryLine) {
        return null;
    }

    return (
        <>
            <TableRow>
                <TableCell>
                    <Button
                        className={classNames(classes.button, { [classes.expanded]: show })}
                        color="inherit"
                        onClick={onExpandClick}
                    >
                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                    </Button>
                </TableCell>
                <TableCell>{journalEntryLinePosition}</TableCell>
                <TableCell className={classNames({ [classes.error]: journalEntryLine.accountError })}>{journalEntryLine.account?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: journalEntryLine.classError })}>{journalEntryLine.class?.value}</TableCell>
                <TableCell><pre className={classes.pre}>{journalEntryLine.description}</pre></TableCell>
                <TableCell className={classNames({ [classes.error]: journalEntryLine.entityError })}>{journalEntryLine.entity?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: journalEntryLine.taxCodeError })}>{journalEntryLine.taxCodeRef?.value}</TableCell>
                <TableCell align="right">
                    {
                        journalEntryLine.postingType === PostingTypeEnum.Debit ?
                            AudCurrencyFormatter.format(-1 * (journalEntryLine.amount ?? 0)) :
                            taxMode === TaxMode.Excluded ?
                                AudCurrencyFormatter.format(journalEntryLine.amount ?? 0) :
                                AudCurrencyFormatter.format(journalEntryLine.taxInclusiveAmount ?? 0)
                    }
                </TableCell>
            </TableRow>
            {
                expanded && (
                    <TableRow>
                        <TableCell className={classes.noPadding} />
                        <TableCell colSpan={6} className={classes.noPadding}>
                            <Collapse in={show} appear={true} onExited={onExited} >
                                <div className={classes.padding}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={3} lg={2}>
                                            <Typography>
                                                <strong>
                                                    Amount
                                                </strong>
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography>
                                                {AudCurrencyFormatter.format((journalEntryLine.postingType === PostingTypeEnum.Debit ? -1 : 1) * (journalEntryLine.amount ?? 0))}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={3} lg={2}>
                                            <Typography>
                                                <strong>
                                                    Tax
                                                </strong>
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography>
                                                {AudCurrencyFormatter.format((journalEntryLine.postingType === PostingTypeEnum.Debit ? -1 : 1) * (journalEntryLine.taxAmount ?? 0))}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )
            }
        </>
    );
}

export default JournalEntryLineView;


// WEBPACK FOOTER //
// ./src/components/JournalEntryLineView.tsx